import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import PeriodSelector from './PeriodSelector';
import DailyDataChart from './DailyDataChart';
import WindDirectionCompass from './WindDirectionCompass';
import MPPTChart from './MPPTChart';
import SmartShuntChart from './SmartShuntChart';
import ACChargingChart from './ACChargingChart';
import HydroUsageChart from './HydroUsageChart';

import './Dashboard.css';

const Dashboard = () => {
  const [stations, setStations] = useState([]);
  const [importBatches, setImportBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState('checking');
  
  // Main tab selection
  const [mainTab, setMainTab] = useState('current');
  
  // Centralized period state
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // 12 Months Starting state (default to Jan of current year)
  const [selected12Year, setSelected12Year] = useState(new Date().getFullYear());
  const [selected12Month, setSelected12Month] = useState(1);
  
  // Chart selection for History tab
  const [selectedChart, setSelectedChart] = useState('daily');
  
  // Current weather data
  const [currentWeather, setCurrentWeather] = useState(null);
  const [currentWeatherLoading, setCurrentWeatherLoading] = useState(false);

  // Current MPPT data
  const [currentMppt, setCurrentMppt] = useState(null);
  const [currentMpptLoading, setCurrentMpptLoading] = useState(false);

  // Current SmartShunt data
  const [currentSmartshunt, setCurrentSmartshunt] = useState(null);
  const [currentSmartshuntLoading, setCurrentSmartshuntLoading] = useState(false);

  // System alerts
  const [systemAlerts, setSystemAlerts] = useState([]);

  const loadAlerts = async () => {
    try {
      const data = await apiService.getAlerts();
      setSystemAlerts(data.alerts || []);
    } catch (err) {
      console.error('Error loading alerts:', err);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await apiService.acknowledgeAlert(alertId);
      setSystemAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  useEffect(() => {
    apiService.setStatusCallback((online) => {
      setApiStatus(online ? 'connected' : 'error');
    });
    loadDashboardData();
    loadCurrentWeather();
    loadCurrentMppt();
    loadCurrentSmartshunt();
    loadAlerts();
    const interval = setInterval(() => {
      loadDashboardData();
      loadCurrentWeather();
      loadCurrentMppt();
      loadCurrentSmartshunt();
      loadAlerts();
    }, 30000);
    return () => {
      clearInterval(interval);
      apiService.setStatusCallback(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCurrentWeather = async () => {
    try {
      setCurrentWeatherLoading(true);
      const data = await apiService.getLatestReading('thepoint_metstream');
      setCurrentWeather(data);
    } catch (err) {
      console.error('Error loading current weather:', err);
    } finally {
      setCurrentWeatherLoading(false);
    }
  };

  const loadCurrentMppt = async () => {
    try {
      setCurrentMpptLoading(true);
      const data = await apiService.getLatestMppt();
      setCurrentMppt(data);
    } catch (err) {
      console.error('Error loading current MPPT:', err);
    } finally {
      setCurrentMpptLoading(false);
    }
  };

  const loadCurrentSmartshunt = async () => {
    try {
      setCurrentSmartshuntLoading(true);
      const data = await apiService.getLatestSmartshunt();
      setCurrentSmartshunt(data);
    } catch (err) {
      console.error('Error loading current SmartShunt:', err);
    } finally {
      setCurrentSmartshuntLoading(false);
    }
  };

  const checkApiConnection = async () => {
    try {
      const response = await fetch('/api/test');
      if (response.ok) {
        setApiStatus('connected');
        return true;
      } else {
        setApiStatus('error');
        return false;
      }
    } catch (err) {
      setApiStatus('disconnected');
      return false;
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const isConnected = await checkApiConnection();
      if (!isConnected) {
        throw new Error('Cannot connect to backend API');
      }

      const stationsData = await apiService.getStations();
      setStations(stationsData);

      const batchesData = await apiService.getImportBatches(5);
      setImportBatches(batchesData);

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (err) {
      return dateString;
    }
  };

  const getApiStatusColor = () => {
    switch (apiStatus) {
      case 'connected': return '#10b981';
      case 'checking': return '#f59e0b';
      case 'error': return '#f97316';
      case 'disconnected': return '#ef4444';
      default: return '#64748b';
    }
  };

  const getApiStatusText = () => {
    switch (apiStatus) {
      case 'connected': return 'Connected';
      case 'checking': return 'Checking...';
      case 'error': return 'Error';
      case 'disconnected': return 'Disconnected';
      default: return 'Unknown';
    }
  };

  if (loading && stations.length === 0) {
    return (
      <div className="dashboard">
        <div className="loading">
          <h2>Loading Dashboard...</h2>
          <p>Connecting to weather stations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="alert alert-error">
          <h3>Dashboard Error</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadDashboardData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatSampleTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }) + ' ET';
  };

  const getWindDirection = (degrees) => {
    if (degrees === null || degrees === undefined) return 'N/A';
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(parseFloat(degrees) / 22.5) % 16;
    return directions[index];
  };

  const clearCacheAndReload = async () => {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      window.location.reload(true);
    } catch (err) {
      console.error('Cache clear error:', err);
      window.location.reload(true);
    }
  };

  return (
    <div className="dashboard">
      {/* System Alert Banner */}
      {systemAlerts.length > 0 && (
        <div className="system-alert-banner">
          {systemAlerts.map(alert => (
            <div key={alert.id} className="system-alert">
              <span className="alert-icon">⚠️</span>
              <span className="alert-message">{alert.message}</span>
              <button 
                className="alert-dismiss"
                onClick={() => acknowledgeAlert(alert.id)}
                title="Dismiss alert"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="title-row">
        <h1 className="main-title">Weather Data Platform</h1>
        <div className="credits-box">
          Constructed by: factory.ai factory-droid[bot]<br />
          as directed by G. Boundy<br />
          Build: {process.env.REACT_APP_BUILD_TIME || 'dev'}
        </div>
        <button 
          className="btn btn-cache-clear" 
          onClick={clearCacheAndReload}
          title="Clear browser cache and reload"
        >
          Clear Cache
        </button>
      </div>

      {/* Main Navigation Tabs */}
      <div className="main-tabs">
        <button 
          className={`main-tab ${mainTab === 'current' ? 'active' : ''}`}
          onClick={() => setMainTab('current')}
        >
          Current Weather
        </button>
        <button 
          className={`main-tab ${mainTab === 'mppt' ? 'active' : ''}`}
          onClick={() => setMainTab('mppt')}
        >
          Current MPPT
        </button>
        <button 
          className={`main-tab ${mainTab === 'smartshunt' ? 'active' : ''}`}
          onClick={() => setMainTab('smartshunt')}
        >
          Current SmartShunt
        </button>
        <button 
          className={`main-tab ${mainTab === 'history' ? 'active' : ''}`}
          onClick={() => setMainTab('history')}
        >
          Weather History
        </button>
        <button 
          className={`main-tab ${mainTab === 'csv' ? 'active' : ''}`}
          onClick={() => setMainTab('csv')}
        >
          CSV History
        </button>
        <button 
          className={`main-tab ${mainTab === 'hydro' ? 'active' : ''}`}
          onClick={() => setMainTab('hydro')}
        >
          Hydro Usage
        </button>
      </div>

      {/* TAB 1: Current Weather */}
      {mainTab === 'current' && (
        <div className="tab-content">
          <div className="dashboard-header">
            <div className="header-title">
              {stations.length > 0 && stations[0].name && (
                <div className="station-info">
                  <span className="station-name">{stations[0].name}</span>
                  {stations[0].latitude && stations[0].longitude && (
                    <span className="station-coords">
                      {parseFloat(stations[0].latitude).toFixed(4)}°N, {parseFloat(stations[0].longitude).toFixed(4)}°W
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="dashboard-status">
              <span className="api-status" style={{ color: getApiStatusColor() }}>
                {getApiStatusText()}
              </span>
              <button className="btn btn-secondary" onClick={() => { loadDashboardData(); loadCurrentWeather(); }}>
                Refresh
              </button>
            </div>
          </div>

          {currentWeatherLoading && !currentWeather ? (
            <div className="loading-card">Loading current weather...</div>
          ) : currentWeather ? (
            <div className="current-weather-display">
              <div className="weather-updated">
                Last Sample Time: {formatSampleTime(currentWeather.recorded_at)}
              </div>
              <div className="weather-grid">
                <div className="weather-card temperature">
                  <div className="weather-label">Temperature</div>
                  <div className="weather-value">{parseFloat(currentWeather.temperature).toFixed(1)}°C</div>
                </div>
                <div className="weather-card humidity">
                  <div className="weather-label">Humidity</div>
                  <div className="weather-value">{parseFloat(currentWeather.humidity).toFixed(0)}%</div>
                </div>
                <div className="weather-card pressure">
                  <div className="weather-label">Pressure</div>
                  <div className="weather-value">{parseFloat(currentWeather.pressure).toFixed(1)} hPa</div>
                </div>
                <div className="weather-card wind">
                  <div className="weather-label">Wind</div>
                  <div className="wind-compass-container">
                    <svg viewBox="0 0 100 100" className="wind-compass">
                      {/* Compass circle */}
                      <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
                      {/* Cardinal directions */}
                      <text x="50" y="12" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="10" fontWeight="bold">N</text>
                      <text x="92" y="54" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9">E</text>
                      <text x="50" y="96" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9">S</text>
                      <text x="8" y="54" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9">W</text>
                      {/* Wind arrow - rotates based on direction, size based on speed */}
                      <g transform={`rotate(${parseFloat(currentWeather.wind_dir) || 0}, 50, 50)`}>
                        <polygon 
                          points={`50,${18 - Math.min(parseFloat(currentWeather.wind_speed) * 2, 10)} 44,50 50,42 56,50`}
                          fill="#10b981"
                        />
                        <line x1="50" y1="50" x2="50" y2={65 + Math.min(parseFloat(currentWeather.wind_speed) * 2, 15)} 
                          stroke="#10b981" strokeWidth="3" strokeLinecap="round"/>
                      </g>
                      {/* Center dot */}
                      <circle cx="50" cy="50" r="4" fill="#10b981"/>
                    </svg>
                    <div className="wind-speed-text">
                      {(parseFloat(currentWeather.wind_speed) * 1.94384).toFixed(1)} kts {getWindDirection(currentWeather.wind_dir)}
                    </div>
                  </div>
                </div>
                <div className="weather-card dewpoint">
                  <div className="weather-label">Dewpoint</div>
                  <div className="weather-value">{parseFloat(currentWeather.dewpoint).toFixed(1)}°C</div>
                </div>
                <div className="weather-card solar">
                  <div className="weather-label">Solar Radiation</div>
                  <div className="weather-value">{parseFloat(currentWeather.solar_radiation).toFixed(0)} W/m²</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <p>No current weather data available.</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Current MPPT */}
      {mainTab === 'mppt' && (
        <div className="tab-content">
          <div className="dashboard-header">
            <div className="header-title">
              <div className="station-info">
                <span className="station-name">Solar Charge Controller (MPPT)</span>
                <span className="station-coords">BT-1 via Bluetooth</span>
              </div>
            </div>
            <div className="dashboard-status">
              <span className="api-status" style={{ color: getApiStatusColor() }}>
                {getApiStatusText()}
              </span>
              <button className="btn btn-secondary" onClick={() => { loadDashboardData(); loadCurrentMppt(); }}>
                Refresh
              </button>
            </div>
          </div>

          {currentMpptLoading && !currentMppt ? (
            <div className="loading-card">Loading current MPPT data...</div>
          ) : currentMppt ? (
            <div className="current-weather-display">
              <div className="weather-updated">
                Last Sample Time: {formatSampleTime(currentMppt.recorded_at)}
                {currentMppt.source && <span className="data-source"> ({currentMppt.source})</span>}
              </div>
              <div className="weather-grid">
                <div className="weather-card battery">
                  <div className="weather-label">Battery</div>
                  <div className="weather-value">{currentMppt.battery_voltage != null ? parseFloat(currentMppt.battery_voltage).toFixed(2) : '-'}V</div>
                  <div className="weather-subvalue">{currentMppt.battery_current != null ? parseFloat(currentMppt.battery_current).toFixed(2) : '-'}A</div>
                </div>
                <div className="weather-card solar">
                  <div className="weather-label">PV Power</div>
                  <div className="weather-value">{currentMppt.pv_power != null ? currentMppt.pv_power : '-'}W</div>
                </div>
                <div className="weather-card load">
                  <div className="weather-label">Load Current</div>
                  <div className="weather-value">{currentMppt.load_current != null ? parseFloat(currentMppt.load_current).toFixed(2) : '-'}A</div>
                </div>
                <div className="weather-card status">
                  <div className="weather-label">Status</div>
                  <div className="weather-value" style={{ fontSize: '1.2rem' }}>{currentMppt.charging_status || '-'}</div>
                </div>
              </div>
              <div className="mppt-stats-row">
                <div className="mppt-stat">
                  <span className="mppt-stat-label">Today Generated:</span>
                  <span className="mppt-stat-value">{currentMppt.watts_generated_today != null ? currentMppt.watts_generated_today : '-'} Wh</span>
                </div>
              </div>
              {(currentMppt.model || currentMppt.battery_type) && (
                <div className="mppt-device-info">
                  {currentMppt.model && <span className="device-model">Model: {currentMppt.model}</span>}
                  {currentMppt.battery_type && <span className="battery-type">Battery: {currentMppt.battery_type}</span>}
                </div>
              )}
            </div>
          ) : (
            <div className="card">
              <p>No current MPPT data available.</p>
            </div>
          )}
        </div>
      )}

      {/* TAB: Current SmartShunt */}
      {mainTab === 'smartshunt' && (
        <div className="tab-content">
          <div className="dashboard-header">
            <div className="header-title">
              <div className="station-info">
                <span className="station-name">Battery Monitor (SmartShunt)</span>
                <span className="station-coords">Victron SmartShunt 500A via Bluetooth</span>
              </div>
            </div>
            <div className="dashboard-status">
              <span className="api-status" style={{ color: getApiStatusColor() }}>
                {getApiStatusText()}
              </span>
              <button className="btn btn-secondary" onClick={() => { loadDashboardData(); loadCurrentSmartshunt(); }}>
                Refresh
              </button>
            </div>
          </div>

          {currentSmartshuntLoading && !currentSmartshunt ? (
            <div className="loading-card">Loading current SmartShunt data...</div>
          ) : currentSmartshunt ? (
            <div className="current-weather-display">
              <div className="weather-updated">
                Last Sample Time: {formatSampleTime(currentSmartshunt.recorded_at)}
                {currentSmartshunt.source && <span className="data-source"> ({currentSmartshunt.source})</span>}
              </div>
              <div className="weather-grid">
                <div className="weather-card battery">
                  <div className="weather-label">SS Voltage</div>
                  <div className="weather-value">{currentSmartshunt.voltage != null ? parseFloat(currentSmartshunt.voltage).toFixed(2) : '--'}V</div>
                </div>
                <div className="weather-card load">
                  <div className="weather-label">SS Current</div>
                  <div className="weather-value">{currentSmartshunt.current != null ? parseFloat(currentSmartshunt.current).toFixed(2) : '--'}A</div>
                </div>
                <div className="weather-card solar">
                  <div className="weather-label">SS Power</div>
                  <div className="weather-value">{currentSmartshunt.power != null ? parseFloat(currentSmartshunt.power).toFixed(1) : '--'}W</div>
                </div>
                <div className="weather-card battery">
                  <div className="weather-label">SS SoC</div>
                  <div className="weather-value">{currentSmartshunt.soc != null ? parseFloat(currentSmartshunt.soc).toFixed(1) : '--'}%</div>
                </div>
                <div className="weather-card temperature">
                  <div className="weather-label">SS Consumed_Ah</div>
                  <div className="weather-value">{currentSmartshunt.consumed_ah != null ? parseFloat(currentSmartshunt.consumed_ah).toFixed(2) : '--'}Ah</div>
                </div>
                <div className="weather-card status">
                  <div className="weather-label">SS Remaining_Mins</div>
                  <div className="weather-value">{currentSmartshunt.remaining_mins != null ? currentSmartshunt.remaining_mins : '--'}</div>
                  <div className="weather-subvalue">{currentSmartshunt.remaining_mins != null ? (currentSmartshunt.remaining_mins / 60).toFixed(1) + ' hrs' : ''}</div>
                </div>
              </div>
              {currentSmartshunt.model && (
                <div className="mppt-device-info">
                  <span className="device-model">Model: {currentSmartshunt.model}</span>
                  {currentSmartshunt.alarm && <span className="battery-type">Alarm: {currentSmartshunt.alarm}</span>}
                </div>
              )}
            </div>
          ) : (
            <div className="card">
              <p>No current SmartShunt data available.</p>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: Weather History */}
      {mainTab === 'history' && (
        <div className="tab-content">
          <div className="dashboard-header">
            <div className="header-title">
              {stations.length > 0 && stations[0].name && (
                <div className="station-info">
                  <span className="station-name">{stations[0].name}</span>
                  {stations[0].latitude && stations[0].longitude && (
                    <span className="station-coords">
                      {parseFloat(stations[0].latitude).toFixed(4)}°N, {parseFloat(stations[0].longitude).toFixed(4)}°W
                    </span>
                  )}
                </div>
              )}
            </div>
            <PeriodSelector
              selectedPeriod={selectedPeriod}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              selected12Year={selected12Year}
              selected12Month={selected12Month}
              onPeriodChange={setSelectedPeriod}
              onYearChange={setSelectedYear}
              onMonthChange={setSelectedMonth}
              on12YearChange={setSelected12Year}
              on12MonthChange={setSelected12Month}
            />
            <div className="dashboard-status">
              <span className="api-status" style={{ color: getApiStatusColor() }}>
                {getApiStatusText()}
              </span>
              <button className="btn btn-secondary" onClick={loadDashboardData}>
                Refresh
              </button>
            </div>
          </div>

          <div className="chart-tabs">
            <button 
              className={`chart-tab ${selectedChart === 'daily' ? 'active' : ''}`}
              onClick={() => setSelectedChart('daily')}
            >
              Daily Data
            </button>
            <button 
              className={`chart-tab ${selectedChart === 'wind' ? 'active' : ''}`}
              onClick={() => setSelectedChart('wind')}
            >
              Wind Direction
            </button>
            <button 
              className={`chart-tab ${selectedChart === 'mppt' ? 'active' : ''}`}
              onClick={() => setSelectedChart('mppt')}
            >
              MPPT
            </button>
            <button 
              className={`chart-tab ${selectedChart === 'smartshunt' ? 'active' : ''}`}
              onClick={() => setSelectedChart('smartshunt')}
            >
              SmartShunt
            </button>
            <button 
              className={`chart-tab ${selectedChart === 'accharger' ? 'active' : ''}`}
              onClick={() => setSelectedChart('accharger')}
            >
              AC Charging
            </button>
          </div>

          <div className="chart-display-region">
            {selectedChart === 'daily' && (
              <DailyDataChart 
                selectedPeriod={selectedPeriod}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selected12Year={selected12Year}
                selected12Month={selected12Month}
              />
            )}

            {selectedChart === 'wind' && (
              <WindDirectionCompass 
                selectedPeriod={selectedPeriod}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selected12Year={selected12Year}
                selected12Month={selected12Month}
              />
            )}

            {selectedChart === 'mppt' && (
              <MPPTChart 
                selectedPeriod={selectedPeriod}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selected12Year={selected12Year}
                selected12Month={selected12Month}
              />
            )}

            {selectedChart === 'smartshunt' && (
              <SmartShuntChart 
                selectedPeriod={selectedPeriod}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selected12Year={selected12Year}
                selected12Month={selected12Month}
              />
            )}

            {selectedChart === 'accharger' && (
              <ACChargingChart 
                selectedPeriod={selectedPeriod}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selected12Year={selected12Year}
                selected12Month={selected12Month}
              />
            )}
          </div>
        </div>
      )}

      {/* TAB 3: CSV Import History */}
      {mainTab === 'csv' && (
        <div className="tab-content">
          <div className="card">
            <h3>CSV Import History</h3>
            {importBatches.length === 0 ? (
              <div className="alert alert-info">
                <p>No import history found.</p>
              </div>
            ) : (
              <div className="import-history">
                {importBatches.map(batch => (
                  <div key={batch.batch_id} className="import-item">
                    <div className="import-info">
                      <div className="import-filename">{batch.filename}</div>
                      <div className="import-details">
                        {batch.imported_records} of {batch.total_records} records imported
                        {batch.failed_records > 0 && (
                          <span className="import-errors"> - {batch.failed_records} errors</span>
                        )}
                      </div>
                    </div>
                    <div className="import-meta">
                      <div className="import-date">{formatDate(batch.created_at)}</div>
                      <div className={`import-status ${batch.status}`}>
                        {batch.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: Hydro Usage */}
      {mainTab === 'hydro' && (
        <div className="tab-content">
          <div className="dashboard-header">
            <div className="header-title">
              <div className="station-info">
                <span className="station-name">Hydro-One Usage Data</span>
                <span className="station-coords">ThePoint - Little Kennisis Lake</span>
              </div>
            </div>
            <div className="dashboard-status">
              <span className="api-status" style={{ color: getApiStatusColor() }}>
                {getApiStatusText()}
              </span>
            </div>
          </div>
          <HydroUsageChart location="ThePoint" />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
